import { Alert, Fade, Snackbar } from "@mui/material";
import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

type ToastContextType = {
    showSuccess: (message: string, duration?: number | null) => void;
    showInfo: (message: string, duration?: number | null) => void;
    showWarning: (message: string, duration?: number | null) => void;
    showError: (message: string, duration?: number | null) => void;
};

type ToastEntry = {
    id: number,
    message: string,
    severity: ToastSeverity,
    duration: number | null
}

enum ToastSeverity {
    success = "success",
    info = "info",
    warning = "warning",
    error = "error"
}

const ToastContext = createContext<ToastContextType | null>(null);

const TOAST_GAP = 8;

/**
 * React component wrapping an UI enabling it to display toast messages
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toastEntries, setToastEntries] = useState<ToastEntry[]>([]);
    const [toastHeights, setToastHeights] = useState<Record<number, number>>({});
    const nextId = useRef<number>(1);
    const toastRefs = useRef<Record<number, HTMLDivElement | null>>({});

    // Convenience functions for adding a new message to toastEntries with the correct severity type
    const showSuccess = (msg: string, duration: number | null = 5000) => setToastEntries((prev: ToastEntry[]) => [...prev, { id: nextId.current++, message: msg, severity: ToastSeverity.success, duration }]);
    const showInfo = (msg: string, duration: number | null = 5000) => setToastEntries((prev: ToastEntry[]) => [...prev, { id: nextId.current++, message: msg, severity: ToastSeverity.info, duration }]);
    const showWarning = (msg: string, duration: number | null = 5000) => setToastEntries((prev: ToastEntry[]) => [...prev, { id: nextId.current++, message: msg, severity: ToastSeverity.warning, duration }]);
    const showError = (msg: string, duration: number | null = 5000) => setToastEntries((prev: ToastEntry[]) => [...prev, { id: nextId.current++, message: msg, severity: ToastSeverity.error, duration }]);

    // Convenience function for removing an entry from toastEntries
    const removeToastEntry = (t: ToastEntry) => { setToastEntries((prev: ToastEntry[]) => prev.filter((p) => p.id !== t.id)); };

    // Reset the id counter if there are no elements, ensuring that we never reach some upper bounds
    useEffect(() => {
        if (toastEntries.length === 0) {
            nextId.current = 1;
        }
    }, [toastEntries]);

    // Construct a Record of toast heights to be able to determine the vertical offset
    // of Toast positions on-screen later
    useLayoutEffect(() => {
        let isChanged = false;
        const newToastHeights: Record<number, number> = {};
        toastEntries.forEach((entry) => {
            const height = toastRefs.current[entry.id]?.getBoundingClientRect().height ?? 0;
            newToastHeights[entry.id] = height;
            if (height !== toastHeights[entry.id]) {
                isChanged = true;
            }
        });
        if (isChanged) {
            setToastHeights(newToastHeights);
        }
    }, [toastEntries]);

    const getToastVerticalOffset = (index: number) => {
        let heightSum = 0;
        for (let i = 0; i < index; i++) {
            heightSum += (toastHeights[toastEntries[i].id] ?? 0) + TOAST_GAP;
        }
        return heightSum;
    };

    return (
        <ToastContext.Provider value={{ showSuccess, showInfo, showWarning, showError }}>
            {children}
            {toastEntries.map((entry: ToastEntry, index: number) => (
                <div key={entry.id}>
                    <Snackbar
                        open={entry.message !== null}
                        slots={{ transition: Fade }}
                        autoHideDuration={entry.duration}
                        onClose={(_, reason) => { if (reason !== "clickaway") removeToastEntry(entry); }}
                        style={{ top: 24 + getToastVerticalOffset(index)}}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                        <Alert
                            ref={(el) => { toastRefs.current[entry.id] = el; }}
                            severity={entry.severity}
                            variant="filled"
                            sx={{ fontSize: "14px", cursor: "pointer" }}
                            onClick={() => removeToastEntry(entry)}
                        >{entry.message ?? ""}</Alert>
                    </Snackbar>
                </div>
            ))}
        </ToastContext.Provider>
    );
}

/**
 * Hook for accessing the functions that display toast messages
 * Usage: `const {showSuccess, showInfo, showWarning, showError} = useToast();`
 * Throws error if used outside the required ToastContext
 */
export function useToast(): ToastContextType {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error("useToast must be used inside a ToastProvider");
    return ctx;
}
